/**
 * Tiny fetch wrapper for the Phoenix JSON API.
 *
 * - All requests use same-origin relative paths like `/api/...`, so there
 *   is no build-time API URL to configure and no CORS in production:
 *   Caddy routes `/api/*` → the Phoenix API container, and the Vite dev
 *   server proxies `/api/*` → `mix phx.server` (see vite.config.ts).
 * - The bearer token is injected automatically from `localStorage` on
 *   every request. A caller may override it per-request via
 *   `opts.token` (pass `null` to force an anonymous request).
 * - Non-2xx responses are surfaced as a structured `ApiRequestError`.
 */
import type { ApiError } from "../lib/types";

const TOKEN_KEY = "thebeat.token";

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* localStorage may be unavailable (private mode); fail soft. */
  }
}

export class ApiRequestError extends Error {
  status: number;
  body: ApiError | null;

  constructor(status: number, message: string, body: ApiError | null = null) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body;
  }
}

export interface RequestOptions {
  /**
   * Bearer token override. When omitted, the token stored in
   * `localStorage` is used. Pass `null` to force an anonymous request.
   */
  token?: string | null;
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
}

async function request<T>(
  method: string,
  path: string,
  body: unknown,
  opts: RequestOptions,
): Promise<T> {
  // Relative same-origin path. Query params are built with URLSearchParams
  // so encoding matches what a browser `fetch` would send.
  const params = new URLSearchParams();
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null) params.set(k, String(v));
    }
  }
  const qs = params.toString();
  const url = `${path}${qs ? `?${qs}` : ""}`;

  // `undefined` => fall back to the stored token; `null` => anonymous.
  const token = opts.token !== undefined ? opts.token : getStoredToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal,
  });

  if (res.status === 204) {
    // 204 No Content — there is no body to parse.
    return undefined as T;
  }

  const text = await res.text();
  const parsed = text ? safeJson(text) : null;

  if (!res.ok) {
    const apiErr = (parsed as ApiError | null) ?? null;
    const message =
      apiErr?.message ?? apiErr?.error ?? defaultMessageFor(res.status);
    throw new ApiRequestError(res.status, message, apiErr);
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function defaultMessageFor(status: number): string {
  switch (status) {
    case 400:
      return "That request was invalid.";
    case 401:
      return "Please sign in to continue.";
    case 403:
      return "You don't have access to that.";
    case 404:
      return "Not found.";
    case 422:
      return "We couldn't save that. Check the details and try again.";
    default:
      return `Request failed (${status}).`;
  }
}

export const api = {
  get: <T>(path: string, opts: RequestOptions = {}) =>
    request<T>("GET", path, undefined, opts),
  post: <T>(path: string, body: unknown, opts: RequestOptions = {}) =>
    request<T>("POST", path, body, opts),
  patch: <T>(path: string, body: unknown, opts: RequestOptions = {}) =>
    request<T>("PATCH", path, body, opts),
  delete: <T>(path: string, opts: RequestOptions = {}) =>
    request<T>("DELETE", path, undefined, opts),
};
